<template>
    <RedesignBaseModal
        :visible="!!group"
        top="8vh"
        append-to-body
        width="720px"
        wrapper-classes="full-height filters-group-modal"
        @update:visible="onChangeVisible"
    >
        <template #title>
            <div class="text-xl px-2">
                {{ t('dashboard.bulkUpdateFilters') }}
            </div>
        </template>
        <div
            v-if="group"
            class="py-4 px-2 space-y-10"
        >
            <template v-if="showTimeFrame || draftConfigs.length">
                <div class="text-base font-semibold main-text-color">
                    {{ t('widget.add.generalForAllWidgets') }}
                </div>

                <RedesignVerticalFormItem
                    v-if="showTimeFrame"
                    :title="String(t('settings.time.frame'))"
                >
                    <template #description>
                        <div class="text-sm font-normal break-keep">
                            {{ t('widget.form.timeInfo.relative') }}
                            <br>
                            {{ t('widget.form.timeInfo.absolute') }}
                            <br>
                            {{ t('widget.form.timeInfo.period') }}
                        </div>
                    </template>
                    <div class="max-w-sm">
                        <RedesignWidgetTimeFrame
                            v-model="draftWidgetTime"
                            :time-frame-type="draftWidgetTime.type"
                            :widget-time-options="widgetTimeOptions"
                        >
                            <template #frame-types>
                                <RedesignRadioGroup
                                    v-model="draftWidgetTime.type"
                                    class="radio-groups mb-4"
                                >
                                    <RedesignRadioButton
                                        v-for="type in createWidgetTimeTypes"
                                        :key="type.value"
                                        :label="type.value"
                                    >
                                        {{ type.label }}
                                    </RedesignRadioButton>
                                </RedesignRadioGroup>
                            </template>
                        </RedesignWidgetTimeFrame>
                    </div>
                </RedesignVerticalFormItem>

                <WidgetTemplatesSettingsList
                    v-if="draftConfigs.length"
                    :configs="draftConfigs"
                    allow-empty-selection
                    @update:config="updateFilterItem"
                />
            </template>
            <div v-else>
                {{ t('dashboard.bulkUpdateFiltersEmpty') }}
            </div>
        </div>
        <template #footer>
            <div class="w-full flex items-center justify-between">
                <RedesignBaseButton
                    type="outline"
                    color="secondary"
                    icon="X"
                    :disabled="loading"
                    @click="closeModal"
                >
                    {{ t('common.cancel') }}
                </RedesignBaseButton>
                <RedesignBaseButton
                    :loading="loading"
                    :disabled="!isDirty"
                    icon="Save"
                    @click="confirm"
                >
                    {{ t('common.save') }}
                </RedesignBaseButton>
            </div>
        </template>
    </RedesignBaseModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignVerticalFormItem from '@/components/RedesignBaseComponents/Form/RedesignVerticalFormItem.vue'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignRadioButton from '@/components/RedesignBaseComponents/Inputs/RedesignRadioButton.vue'
import RedesignWidgetTimeFrame from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetTimeFrame.vue'
import WidgetTemplatesSettingsList
    from '@/components/RedesignComponents/Widgets/Create/WidgetTemplatesSettingsList.vue'
import { widgetTimeOptions, widgetTimeTypes } from '@/enum/widgetTimeOptions'
import { WidgetConfig, WidgetGroupList, WidgetTime } from '@/types/dashboard'
import { shouldShowTimeFrameForWidgets } from '@/helpers/widgetUtils'
import type { WidgetParameterBulkUpdatePayload } from '@/api/widgetParameterApi'
import {
    buildBulkWidgetFiltersPayload,
    DEFAULT_WIDGET_TIME,
    getUniqEntityFilterStubs,
    isBulkFiltersFormDirty
} from '@/helpers/bulkUpdateWidgetFilters'
import i18n from '@/i18n'

const t = i18n.t.bind(i18n)

const props = defineProps<{
    group: WidgetGroupList | null
    loading?: boolean
}>()

const emit = defineEmits<{
    (e: 'save', payload: WidgetParameterBulkUpdatePayload): void
    (e: 'close'): void
}>()

const { closeWithConfirm } = useConfirmBeforeClose()

const draftConfigs = ref<WidgetConfig[]>([])
const draftWidgetTime = ref<WidgetTime>({ ...DEFAULT_WIDGET_TIME })

const createWidgetTimeTypes = computed(() => {
    return widgetTimeTypes.map(el => ({
        label: t(el.text),
        value: el.label
    }))
})

const showTimeFrame = computed(() =>
    !!props.group?.WidgetList?.length && shouldShowTimeFrameForWidgets(props.group.WidgetList)
)

const isDirty = computed(() => {
    return isBulkFiltersFormDirty(draftConfigs.value, draftWidgetTime.value)
})

function updateFilterItem (config: WidgetConfig) {
    draftConfigs.value = draftConfigs.value.map(item =>
        item.ParameterName === config.ParameterName ? { ...config } : item
    )
}

function onChangeVisible (state: boolean) {
    if (!state) {
        closeModal()
    }
}

function closeModal () {
    if (props.loading) return

    if (isDirty.value) {
        closeWithConfirm(() => emit('close'))
        return
    }
    emit('close')
}

function confirm () {
    if (!props.group) {
        return
    }

    const payload = buildBulkWidgetFiltersPayload({
        group: props.group,
        draftConfigs: draftConfigs.value,
        draftWidgetTime: draftWidgetTime.value
    })

    if (!payload.Widgets.length) {
        emit('close')
        return
    }

    emit('save', payload)
}

watch(
    () => props.group,
    (group) => {
        draftWidgetTime.value = { ...DEFAULT_WIDGET_TIME }
        draftConfigs.value = group ? getUniqEntityFilterStubs(group) : []
    },
    { immediate: true }
)
</script>
